import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  Bell,
  BriefcaseBusiness,
  CalendarDays,
  ChevronDown,
  Contact,
  CreditCard,
  Loader2,
  Mail,
  MessageSquare,
  Monitor,
  Pencil,
  RotateCcw,
  Save,
  Settings2,
  Smartphone,
  TrendingUp,
  UserCog,
  Users,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { NumberInput } from '@/components/molecules/NumberInput';
import { Switch } from '@/components/atoms/switch';
import { DetailPageLayout } from '@/components/layouts';
import { SettingsPageHeader } from '@/components/layouts/SettingsPageHeader';
import { Fullbleed } from '@/contexts/fullbleed';
import {
  useNotificationPreferences,
  useOperationalNotificationRules,
  useResetNotificationPreferences,
  useUpdateNotificationPreferences,
  useUpdateOperationalNotificationRules,
} from '@/hooks/notifications/useStaffNotifications';
import { cn } from '@/lib/utils';
import { useAuthPermissions } from '@/stores/auth.store';
import type {
  NotificationCategory,
  NotificationChannelKey,
  NotificationPreferenceChannels,
  NotificationPreferenceEvent,
  OperationalNotificationRules,
} from '@/types/notifications';

export const Route = createFileRoute('/_dashboard/dashboard/settings/notifications')({
  component: NotificationsSettingsPage,
});

const CHANNELS: Array<{
  key: NotificationChannelKey;
  label: string;
  Icon: typeof Bell;
}> = [
  { key: 'inApp', label: 'In app', Icon: Bell },
  { key: 'email', label: 'Email', Icon: Mail },
  { key: 'sms', label: 'SMS', Icon: MessageSquare },
  { key: 'webPush', label: 'Web push', Icon: Monitor },
  { key: 'mobilePush', label: 'Mobile', Icon: Smartphone },
];

const CATEGORY_META: Record<
  NotificationCategory,
  { label: string; description: string; Icon: typeof Bell }
> = {
  billing: {
    label: 'Billing',
    description: 'Payments, invoices, renewals, and collections',
    Icon: CreditCard,
  },
  scheduling: {
    label: 'Scheduling',
    description: 'Visits, assignments, confirmations, and dispatch',
    Icon: CalendarDays,
  },
  sales: {
    label: 'Sales',
    description: 'Estimates, proposals, and approvals',
    Icon: TrendingUp,
  },
  leads: {
    label: 'Leads',
    description: 'Assignments, routing, and response SLAs',
    Icon: Users,
  },
  jobs: {
    label: 'Jobs',
    description: 'Job progress, reports, and operational blockers',
    Icon: BriefcaseBusiness,
  },
  contacts: {
    label: 'Contacts',
    description: 'Customer records and follow-up activity',
    Icon: Contact,
  },
  team: {
    label: 'Team',
    description: 'People, approvals, and team activity',
    Icon: UserCog,
  },
  system: {
    label: 'System',
    description: 'Integrations, automation, and account health',
    Icon: Settings2,
  },
};

const CATEGORY_ORDER: NotificationCategory[] = [
  'billing',
  'scheduling',
  'sales',
  'leads',
  'jobs',
  'contacts',
  'team',
  'system',
];

type Draft = Record<string, NotificationPreferenceChannels>;

function NotificationsSettingsPage() {
  const permissions = useAuthPermissions();
  const canViewOperationalRules = permissions.includes('notifications:view');
  const canManageOperationalRules = permissions.includes('notifications:manage');
  const prefsQuery = useNotificationPreferences();
  const updatePrefs = useUpdateNotificationPreferences();
  const resetPrefs = useResetNotificationPreferences();
  const rulesQuery = useOperationalNotificationRules({ enabled: canViewOperationalRules });
  const updateRules = useUpdateOperationalNotificationRules();
  const [draft, setDraft] = useState<Draft>({});
  const [operationalDraft, setOperationalDraft] =
    useState<OperationalNotificationRules>(OPERATIONAL_RULE_DEFAULTS);
  const [editing, setEditing] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState<NotificationCategory | null>(null);
  const preferenceEvents = prefsQuery.data?.events;

  useEffect(() => {
    if (!preferenceEvents || editing) return;
    setDraft(
      Object.fromEntries(preferenceEvents.map((event) => [event.eventType, { ...event.channels }])),
    );
  }, [editing, preferenceEvents]);

  useEffect(() => {
    if (!rulesQuery.data || editing) return;
    setOperationalDraft(rulesQuery.data);
  }, [editing, rulesQuery.data]);

  const events = useMemo(
    () =>
      (preferenceEvents ?? []).map((event) => ({
        ...event,
        category: event.category ?? inferLegacyCategory(event.eventType),
        priority: event.priority ?? 'fyi',
      })),
    [preferenceEvents],
  );
  const groups = useMemo(
    () =>
      CATEGORY_ORDER.map((category) => ({
        category,
        events: events.filter((event) => event.category === category),
      })).filter((group) => group.events.length > 0),
    [events],
  );
  const preferenceDirty = useMemo(
    () =>
      events.some((event) =>
        CHANNELS.some(
          (channel) => draft[event.eventType]?.[channel.key] !== event.channels[channel.key],
        ),
      ),
    [draft, events],
  );
  const operationalDirty = Boolean(
    canManageOperationalRules &&
    rulesQuery.data &&
    (Object.keys(OPERATIONAL_RULE_DEFAULTS) as Array<keyof OperationalNotificationRules>).some(
      (key) => rulesQuery.data?.[key] !== operationalDraft[key],
    ),
  );
  const operationalValid =
    operationalDraft.unbilledAfterDays >= 1 &&
    operationalDraft.unbilledAfterDays <= 3650 &&
    operationalDraft.pastWindowMinutes >= 1 &&
    operationalDraft.pastWindowMinutes <= 1440 &&
    operationalDraft.receivableAfterDays >= 1 &&
    operationalDraft.receivableAfterDays <= 3650 &&
    /^(?:[01][0-9]|2[0-3]):[0-5][0-9]$/.test(operationalDraft.tomorrowCheckTime);
  const dirty = preferenceDirty || operationalDirty;
  const saving = updatePrefs.isPending || updateRules.isPending;
  // Reset deletes every saved row; Save writes one back per event. If they
  // overlap, the PATCH restores exactly what the reset just dropped and the
  // user is left with a "reset succeeded" toast over re-pinned server state —
  // so the whole action row locks while either request is in flight.
  const busy = saving || resetPrefs.isPending;
  const enabledEvents = events.filter((event) =>
    CHANNELS.some(({ key }) => (draft[event.eventType] ?? event.channels)[key]),
  ).length;
  const activeChannels = CHANNELS.filter(({ key }) =>
    events.some((event) => (draft[event.eventType] ?? event.channels)[key]),
  ).length;
  const exceptionCount = groups.reduce((count, group) => {
    const baseline = deriveCategoryRule(group.events, draft);
    return count + getExceptions(group.events, draft, baseline).length;
  }, 0);

  const resetDraft = () => {
    setDraft(Object.fromEntries(events.map((event) => [event.eventType, { ...event.channels }])));
    if (rulesQuery.data) setOperationalDraft(rulesQuery.data);
  };

  const cancel = () => {
    resetDraft();
    setEditing(false);
  };

  const save = async () => {
    try {
      if (preferenceDirty) {
        await updatePrefs.mutateAsync({
          preferences: events.map((event) => ({
            eventType: event.eventType,
            ...draft[event.eventType],
          })),
        });
      }
      if (operationalDirty) await updateRules.mutateAsync(operationalDraft);
      toast.success('Notification settings saved');
      setEditing(false);
    } catch {
      const [freshPreferences, freshRules] = await Promise.all([
        prefsQuery.refetch(),
        canViewOperationalRules ? rulesQuery.refetch() : Promise.resolve(null),
      ]);
      const latestEvents = freshPreferences.data?.events ?? preferenceEvents ?? [];
      setDraft(
        Object.fromEntries(latestEvents.map((event) => [event.eventType, { ...event.channels }])),
      );
      if (freshRules?.data) setOperationalDraft(freshRules.data);
      toast.error('Some settings may have saved. Latest settings were reloaded.');
    }
  };

  /**
   * Saving writes a row for every event, so anyone who has ever saved is pinned
   * to whatever the defaults were that day. Dropping the rows is what puts them
   * back on the current recommended set.
   */
  const resetToDefaults = async () => {
    try {
      const fresh = await resetPrefs.mutateAsync();
      setDraft(
        Object.fromEntries(fresh.events.map((event) => [event.eventType, { ...event.channels }])),
      );
      toast.success('Notification preferences reset to the defaults');
    } catch {
      toast.error('Could not reset notification preferences');
    }
  };

  const setOperationalRule = <Key extends keyof OperationalNotificationRules>(
    key: Key,
    value: OperationalNotificationRules[Key],
  ) => setOperationalDraft((current) => ({ ...current, [key]: value }));

  const setEventChannel = (
    event: NotificationPreferenceEvent,
    channel: NotificationChannelKey,
    checked: boolean,
  ) => {
    setDraft((current) => ({
      ...current,
      [event.eventType]: {
        ...(current[event.eventType] ?? event.channels),
        [channel]: checked,
      },
    }));
  };

  const setCategoryChannel = (
    categoryEvents: NotificationPreferenceEvent[],
    channel: NotificationChannelKey,
    checked: boolean,
  ) => {
    setDraft((current) => {
      const next = { ...current };
      categoryEvents.forEach((event) => {
        next[event.eventType] = {
          ...(current[event.eventType] ?? event.channels),
          [channel]: checked,
        };
      });
      return next;
    });
  };

  return (
    <>
      <Fullbleed />
      <DetailPageLayout
        header={
          <SettingsPageHeader
            title="Notifications"
            meta={
              editing ? (
                <span className="hidden text-xs text-muted-foreground sm:inline">Editing</span>
              ) : prefsQuery.data?.summary?.lastSavedAt ? (
                <span className="hidden text-xs text-muted-foreground sm:inline">
                  Saved {formatSavedAt(prefsQuery.data.summary.lastSavedAt)}
                </span>
              ) : null
            }
            actions={
              editing ? (
                <>
                  {dirty ? (
                    <span className="hidden text-xs text-muted-foreground sm:inline">
                      Unsaved changes
                    </span>
                  ) : null}
                  <Button
                    variant="outline"
                    size="sm"
                    className="px-2 sm:px-3"
                    onClick={() => void resetToDefaults()}
                    disabled={busy}
                    aria-label="Reset notification preferences to the defaults"
                  >
                    {resetPrefs.isPending ? (
                      <Loader2 className="size-3.5 animate-spin" />
                    ) : (
                      <RotateCcw className="size-3.5" />
                    )}
                    <span className="sr-only sm:not-sr-only">Reset to defaults</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="px-2 sm:px-3"
                    onClick={cancel}
                    disabled={busy}
                    aria-label="Cancel notification setting changes"
                  >
                    <X className="size-3.5" />
                    <span className="sr-only sm:not-sr-only">Cancel</span>
                  </Button>
                  <Button
                    size="sm"
                    className="px-2 sm:px-3"
                    onClick={() => void save()}
                    disabled={!dirty || !operationalValid || busy}
                    aria-label="Save notification setting changes"
                  >
                    {saving ? (
                      <Loader2 className="size-3.5 animate-spin" />
                    ) : (
                      <Save className="size-3.5" />
                    )}
                    <span className="sr-only sm:not-sr-only">Save changes</span>
                  </Button>
                </>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditing(true)}
                  disabled={
                    !preferenceEvents || (canManageOperationalRules && rulesQuery.isLoading)
                  }
                >
                  <Pencil className="size-3.5" />
                  Edit
                </Button>
              )
            }
          />
        }
        headerClassName="min-h-[57px] px-4 py-0 sm:px-6"
        contentClassName="bg-[#f4f4f1] p-0"
      >
        <main className="mx-auto w-full max-w-[1600px] space-y-4 p-4 sm:p-5">
          {prefsQuery.isLoading ? (
            <LoadingState />
          ) : prefsQuery.isError ? (
            <div className="rounded-xl border bg-card px-5 py-12 text-center">
              <p className="text-sm font-medium">Couldn&apos;t load notification settings</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-4"
                onClick={() => prefsQuery.refetch()}
              >
                Try again
              </Button>
            </div>
          ) : events.length === 0 ? (
            <div className="rounded-xl border bg-card px-5 py-12 text-center text-sm text-muted-foreground">
              No notification events are configured.
            </div>
          ) : (
            <>
              {canViewOperationalRules ? (
                <OperationalAlertsSection
                  editing={editing && canManageOperationalRules}
                  draft={operationalDraft}
                  valid={operationalValid}
                  isLoading={rulesQuery.isLoading}
                  isError={rulesQuery.isError}
                  onRetry={() => rulesQuery.refetch()}
                  onChange={setOperationalRule}
                />
              ) : null}

              <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <SummaryCard
                  eyebrow="Volume"
                  value={
                    prefsQuery.data?.summary
                      ? String(prefsQuery.data.summary.notificationsLast7Days)
                      : '—'
                  }
                  title={
                    prefsQuery.data?.summary
                      ? 'notification entries created for you in the last 7 days'
                      : 'notification volume is not available yet'
                  }
                  detail={`${events.length} available events`}
                />
                <SummaryCard
                  eyebrow="Delivery"
                  value={`${enabledEvents}/${events.length}`}
                  title="events have at least one channel on"
                  detail={`${activeChannels} of ${CHANNELS.length} channels in use`}
                />
                <SummaryCard
                  eyebrow="Categories"
                  value={String(groups.length)}
                  title="notification groups"
                  detail="Open one to see its individual events"
                />
                <SummaryCard
                  eyebrow="Exceptions"
                  value={String(exceptionCount)}
                  title="events differ from their category rule"
                  detail="Only personal delivery choices are counted"
                />
              </section>

              <section className="overflow-hidden rounded-xl border bg-card shadow-sm">
                <div className="flex flex-wrap items-baseline justify-between gap-2 border-b px-4 py-3.5">
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <h2 className="text-sm font-semibold">Delivery by category</h2>
                    <p className="text-xs text-muted-foreground">
                      {editing
                        ? 'Set a category rule, then keep only the exceptions you need.'
                        : 'Open a category to see which events differ from its rule.'}
                    </p>
                  </div>
                  <span className="rounded-md bg-muted px-2 py-1 text-[11px] text-muted-foreground">
                    {exceptionCount} exceptions
                  </span>
                </div>

                <div className="hidden grid-cols-[minmax(15rem,1.5fr)_repeat(5,minmax(5.5rem,0.65fr))] border-b bg-muted/45 px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground md:grid">
                  <div>Category</div>
                  {CHANNELS.map(({ key, label, Icon }) => (
                    <div key={key} className="flex items-center justify-center gap-1.5">
                      <Icon className="size-3" />
                      <span>{label}</span>
                    </div>
                  ))}
                </div>

                {groups.map((group) => (
                  <CategorySection
                    key={group.category}
                    category={group.category}
                    events={group.events}
                    draft={draft}
                    editing={editing}
                    expanded={expandedCategory === group.category}
                    onToggle={() =>
                      setExpandedCategory((current) =>
                        current === group.category ? null : group.category,
                      )
                    }
                    onEventChannelChange={setEventChannel}
                    onCategoryChannelChange={(channel, checked) =>
                      setCategoryChannel(group.events, channel, checked)
                    }
                  />
                ))}
              </section>
            </>
          )}
        </main>
      </DetailPageLayout>
    </>
  );
}

const OPERATIONAL_RULE_DEFAULTS: OperationalNotificationRules = {
  unbilledEnabled: true,
  unbilledAfterDays: 2,
  pastWindowEnabled: true,
  pastWindowMinutes: 30,
  receivableAgingEnabled: true,
  receivableAfterDays: 60,
  tomorrowExceptionsEnabled: true,
  tomorrowCheckTime: '17:00',
};

function OperationalAlertsSection({
  editing,
  draft,
  valid,
  isLoading,
  isError,
  onRetry,
  onChange,
}: {
  editing: boolean;
  draft: OperationalNotificationRules;
  valid: boolean;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onChange: <Key extends keyof OperationalNotificationRules>(
    key: Key,
    value: OperationalNotificationRules[Key],
  ) => void;
}) {
  return (
    <section className="overflow-hidden rounded-xl border bg-card shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3.5">
        <div className="min-w-0">
          <div className="flex flex-wrap items-baseline gap-2">
            <h2 className="text-sm font-semibold">Operations alerts</h2>
            <span className="text-xs text-muted-foreground">
              Decide when an operational exception becomes a notification.
            </span>
          </div>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            {editing
              ? 'Use Save in the page header to apply rule and delivery changes.'
              : 'Use Edit above to change rules; delivery channels follow the preferences below.'}
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="grid gap-px bg-border sm:grid-cols-2 xl:grid-cols-5">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="h-[94px] animate-pulse bg-card p-4">
              <div className="h-3 w-2/3 rounded bg-muted" />
              <div className="mt-3 h-7 w-1/2 rounded bg-muted" />
            </div>
          ))}
        </div>
      ) : isError ? (
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-4">
          <p className="text-xs text-muted-foreground">
            Operations alert timing could not be loaded.
          </p>
          <Button variant="outline" size="sm" onClick={onRetry}>
            Try again
          </Button>
        </div>
      ) : (
        <div className="grid gap-px bg-border sm:grid-cols-2 xl:grid-cols-5">
          <OperationalRuleCell
            title="Finished work not billed"
            description="Wait after job completion"
            enabled={draft.unbilledEnabled}
            editing={editing}
            onEnabledChange={(value) => onChange('unbilledEnabled', value)}
          >
            <RuleNumberInput
              value={draft.unbilledAfterDays}
              max={3650}
              unit="days"
              editing={editing}
              enabled={draft.unbilledEnabled}
              onChange={(value) => onChange('unbilledAfterDays', value)}
            />
          </OperationalRuleCell>
          <OperationalRuleCell
            title="Visit past its window"
            description="Wait after promised end"
            enabled={draft.pastWindowEnabled}
            editing={editing}
            onEnabledChange={(value) => onChange('pastWindowEnabled', value)}
          >
            <RuleNumberInput
              value={draft.pastWindowMinutes}
              max={1440}
              unit="min"
              editing={editing}
              enabled={draft.pastWindowEnabled}
              onChange={(value) => onChange('pastWindowMinutes', value)}
            />
          </OperationalRuleCell>
          <OperationalRuleCell
            title="Receivables aging"
            description="Wait after invoice is due"
            enabled={draft.receivableAgingEnabled}
            editing={editing}
            onEnabledChange={(value) => onChange('receivableAgingEnabled', value)}
          >
            <RuleNumberInput
              value={draft.receivableAfterDays}
              max={3650}
              unit="days"
              editing={editing}
              enabled={draft.receivableAgingEnabled}
              onChange={(value) => onChange('receivableAfterDays', value)}
            />
          </OperationalRuleCell>
          <OperationalRuleCell
            title="Tomorrow's exceptions"
            description="Daily check in company time"
            enabled={draft.tomorrowExceptionsEnabled}
            editing={editing}
            onEnabledChange={(value) => onChange('tomorrowExceptionsEnabled', value)}
          >
            <RuleTimeInput
              value={draft.tomorrowCheckTime}
              editing={editing}
              enabled={draft.tomorrowExceptionsEnabled}
              valid={valid}
              onChange={(value) => onChange('tomorrowCheckTime', value)}
            />
          </OperationalRuleCell>
          <div className="min-w-0 bg-card px-4 py-3.5">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-semibold">Automation failure</p>
              <span className="rounded-md bg-destructive-surface px-2 py-1 text-[10px] font-medium text-destructive">
                Immediate
              </span>
            </div>
            <p className="mt-1 text-[11px] leading-4 text-muted-foreground">
              Channels are controlled by <span className="font-medium">Reminder failed</span> below.
            </p>
          </div>
        </div>
      )}
    </section>
  );
}

function OperationalRuleCell({
  title,
  description,
  enabled,
  editing,
  onEnabledChange,
  children,
}: {
  title: string;
  description: string;
  enabled: boolean;
  editing: boolean;
  onEnabledChange: (value: boolean) => void;
  children: ReactNode;
}) {
  return (
    <div className="min-w-0 bg-card px-4 py-3.5">
      <div className="flex items-center justify-between gap-2">
        <p className="truncate text-xs font-semibold">{title}</p>
        {editing ? (
          <Switch
            checked={enabled}
            onCheckedChange={onEnabledChange}
            aria-label={`${enabled ? 'Disable' : 'Enable'} ${title}`}
            className="scale-90"
          />
        ) : (
          <span
            className={cn(
              'rounded-full px-2 py-0.5 text-[10px] font-medium',
              enabled ? 'bg-success-surface text-success' : 'bg-muted text-muted-foreground',
            )}
          >
            {enabled ? 'On' : 'Off'}
          </span>
        )}
      </div>
      <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{description}</p>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function RuleNumberInput({
  value,
  max,
  unit,
  editing,
  enabled,
  onChange,
}: {
  value: number;
  max: number;
  unit: string;
  editing: boolean;
  enabled: boolean;
  onChange: (value: number) => void;
}) {
  if (!editing) {
    return (
      <p className="text-xs text-muted-foreground">
        After <span className="font-medium tabular-nums text-foreground">{value}</span> {unit}
      </p>
    );
  }

  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <span>After</span>
      <NumberInput
        min={1}
        max={max}
        step={1}
        value={value}
        disabled={!enabled}
        // Clearing the box parks the threshold at its floor of 1 rather than
        // refusing the keystroke; blur settles the box on whatever was kept.
        emptyValue={1}
        onChange={(next) => onChange(Math.min(max, Math.max(1, next ?? 1)))}
        className="h-7 w-16 px-2 text-xs tabular-nums"
        aria-label={`${unit} before alerting`}
      />
      <span>{unit}</span>
    </div>
  );
}

function RuleTimeInput({
  value,
  editing,
  enabled,
  valid,
  onChange,
}: {
  value: string;
  editing: boolean;
  enabled: boolean;
  valid: boolean;
  onChange: (value: string) => void;
}) {
  if (!editing) {
    return (
      <p className="text-xs text-muted-foreground">
        At <span className="font-medium tabular-nums text-foreground">{formatRuleTime(value)}</span>
      </p>
    );
  }

  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <span>At</span>
      <Input
        type="time"
        value={value}
        required
        aria-invalid={!valid}
        disabled={!enabled}
        onChange={(event) => onChange(event.target.value)}
        className="h-7 w-[104px] px-2 text-xs"
        aria-label="Tomorrow exceptions check time"
      />
    </div>
  );
}

function formatRuleTime(value: string) {
  const [hourText, minute = '00'] = value.split(':');
  const hour = Number(hourText);
  if (!Number.isInteger(hour) || hour < 0 || hour > 23) return value;
  return `${hour % 12 || 12}:${minute} ${hour < 12 ? 'AM' : 'PM'}`;
}

function SummaryCard({
  eyebrow,
  value,
  title,
  detail,
}: {
  eyebrow: string;
  value: string;
  title: string;
  detail: string;
}) {
  return (
    <div className="rounded-xl border bg-card px-4 py-3.5 shadow-sm">
      <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
        {eyebrow}
      </p>
      <div className="mt-1.5 flex items-baseline gap-2">
        <span className="text-2xl font-semibold tracking-[-0.04em]">{value}</span>
        <span className="text-sm font-medium">{title}</span>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">{detail}</p>
    </div>
  );
}

function CategorySection({
  category,
  events,
  draft,
  editing,
  expanded,
  onToggle,
  onEventChannelChange,
  onCategoryChannelChange,
}: {
  category: NotificationCategory;
  events: NotificationPreferenceEvent[];
  draft: Draft;
  editing: boolean;
  expanded: boolean;
  onToggle: () => void;
  onEventChannelChange: (
    event: NotificationPreferenceEvent,
    channel: NotificationChannelKey,
    checked: boolean,
  ) => void;
  onCategoryChannelChange: (channel: NotificationChannelKey, checked: boolean) => void;
}) {
  const meta = CATEGORY_META[category];
  const baseline = deriveCategoryRule(events, draft);
  const exceptions = getExceptions(events, draft, baseline);
  const hasMixedRule = CHANNELS.some(({ key }) => baseline[key] === undefined);
  const visibleEvents = editing || hasMixedRule ? events : exceptions;
  const { Icon } = meta;

  return (
    <div className="border-b last:border-b-0">
      <div className="hidden grid-cols-[minmax(15rem,1.5fr)_repeat(5,minmax(5.5rem,0.65fr))] items-stretch md:grid">
        <button
          type="button"
          onClick={onToggle}
          className="flex min-w-0 items-center gap-3 px-4 py-3 text-left hover:bg-muted/25"
          aria-expanded={expanded}
        >
          <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-muted text-muted-foreground">
            <Icon className="size-4" />
          </span>
          <span className="min-w-0">
            <span className="flex items-center gap-1.5">
              <span className="text-sm font-semibold">{meta.label}</span>
              <span className="text-xs text-muted-foreground">
                · {events.length} {events.length === 1 ? 'event' : 'events'}
              </span>
              {exceptions.length > 0 ? (
                <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                  {exceptions.length} changed
                </span>
              ) : hasMixedRule ? (
                <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                  Mixed
                </span>
              ) : null}
            </span>
            <span className="mt-0.5 block truncate text-xs text-muted-foreground">
              {meta.description}
            </span>
          </span>
          <ChevronDown
            className={cn(
              'ml-auto size-4 shrink-0 text-muted-foreground transition-transform',
              expanded && 'rotate-180',
            )}
          />
        </button>
        {CHANNELS.map(({ key, label }) => (
          <CategoryChannelControl
            key={key}
            label={`${meta.label} ${label}`}
            events={events}
            draft={draft}
            channel={key}
            editing={editing}
            onChange={(checked) => onCategoryChannelChange(key, checked)}
          />
        ))}
      </div>

      <button
        type="button"
        onClick={onToggle}
        className="w-full px-4 py-3 text-left md:hidden"
        aria-expanded={expanded}
      >
        <span className="flex items-center gap-3">
          <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-muted text-muted-foreground">
            <Icon className="size-4" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold">{meta.label}</span>
            <span className="block text-xs text-muted-foreground">
              {events.length} {events.length === 1 ? 'event' : 'events'}
              {exceptions.length
                ? ` · ${exceptions.length} exceptions`
                : hasMixedRule
                  ? ' · Mixed'
                  : ''}
            </span>
          </span>
          <span className="flex items-center gap-2">
            {CHANNELS.map(({ key, label }) => (
              <ChannelDot
                key={key}
                label={label}
                enabled={channelCount(events, draft, key) > 0}
                partial={
                  channelCount(events, draft, key) > 0 &&
                  channelCount(events, draft, key) < events.length
                }
              />
            ))}
            <ChevronDown
              className={cn(
                'size-4 text-muted-foreground transition-transform',
                expanded && 'rotate-180',
              )}
            />
          </span>
        </span>
      </button>

      {expanded ? (
        <div className="border-t bg-muted/25">
          <div className="border-b px-4 py-2.5 md:hidden">
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              Rule for all {events.length}
            </p>
            <div className="space-y-2.5">
              {CHANNELS.map(({ key, label, Icon: ChannelIcon }) => {
                const count = channelCount(events, draft, key);
                return (
                  <div key={key} className="flex items-center justify-between gap-3">
                    <span className="flex items-center gap-2 text-sm">
                      <ChannelIcon className="size-3.5 text-muted-foreground" />
                      {label}
                      {count > 0 && count < events.length ? (
                        <span className="text-xs text-muted-foreground">
                          {count} of {events.length}
                        </span>
                      ) : null}
                    </span>
                    <Switch
                      size="sm"
                      checked={count === events.length}
                      disabled={!editing}
                      onCheckedChange={(checked) => onCategoryChannelChange(key, checked)}
                      aria-label={`${meta.label} ${label}`}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          <div className="px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            {editing
              ? `All ${events.length} events`
              : hasMixedRule
                ? 'Mixed settings'
                : `${exceptions.length} exceptions`}
          </div>
          {visibleEvents.length === 0 ? (
            <p className="border-t px-4 py-4 text-sm text-muted-foreground">
              Every event follows the category rule.
            </p>
          ) : (
            visibleEvents.map((event) => (
              <EventPreferenceRow
                key={event.eventType}
                event={event}
                draft={draft[event.eventType] ?? event.channels}
                editing={editing}
                onChange={(channel, checked) => onEventChannelChange(event, channel, checked)}
              />
            ))
          )}
          {!editing && !hasMixedRule && exceptions.length < events.length ? (
            <p className="border-t px-4 py-3 text-xs text-muted-foreground">
              The other {events.length - exceptions.length} events follow the category rule above.
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function CategoryChannelControl({
  label,
  events,
  draft,
  channel,
  editing,
  onChange,
}: {
  label: string;
  events: NotificationPreferenceEvent[];
  draft: Draft;
  channel: NotificationChannelKey;
  editing: boolean;
  onChange: (checked: boolean) => void;
}) {
  const count = channelCount(events, draft, channel);
  return (
    <div className="flex items-center justify-center border-l border-border/70 px-2 py-3">
      {editing ? (
        <div className="flex flex-col items-center gap-1">
          <Switch
            size="sm"
            checked={count === events.length}
            onCheckedChange={onChange}
            aria-label={label}
          />
          <span className="text-[10px] text-muted-foreground">
            {count === events.length ? 'All' : count === 0 ? 'Off' : `${count}/${events.length}`}
          </span>
        </div>
      ) : (
        <span
          className={cn(
            'min-w-7 border-b-2 px-1 pb-0.5 text-center text-xs font-medium',
            count === events.length
              ? 'border-foreground text-foreground'
              : count === 0
                ? 'border-transparent text-muted-foreground/50'
                : 'border-muted-foreground/40 text-muted-foreground',
          )}
        >
          {count === events.length ? 'All' : count === 0 ? '—' : count}
        </span>
      )}
    </div>
  );
}

function EventPreferenceRow({
  event,
  draft,
  editing,
  onChange,
}: {
  event: NotificationPreferenceEvent;
  draft: NotificationPreferenceChannels;
  editing: boolean;
  onChange: (channel: NotificationChannelKey, checked: boolean) => void;
}) {
  return (
    <div className="border-t px-4 py-3 md:grid md:grid-cols-[minmax(15rem,1.5fr)_repeat(5,minmax(5.5rem,0.65fr))] md:items-center md:px-0 md:py-0">
      <div className="min-w-0 md:px-4 md:py-3">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'size-1.5 shrink-0 rounded-full',
              event.priority === 'action'
                ? 'bg-destructive'
                : event.priority === 'attention'
                  ? 'bg-warning'
                  : 'bg-muted-foreground/50',
            )}
          />
          <p className="truncate text-sm font-medium">{event.label}</p>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-4 md:contents">
        {CHANNELS.map(({ key, label, Icon }) => (
          <div
            key={key}
            className="flex items-center justify-center md:border-l md:border-border/70 md:py-3"
          >
            {editing ? (
              <Switch
                size="sm"
                checked={draft[key]}
                onCheckedChange={(checked) => onChange(key, checked)}
                aria-label={`${event.label} ${label}`}
              />
            ) : (
              <span title={label} className="grid size-6 place-items-center">
                <Icon
                  className={cn(
                    'size-3.5',
                    draft[key] ? 'text-foreground' : 'text-muted-foreground/25',
                  )}
                />
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ChannelDot({
  label,
  enabled,
  partial,
}: {
  label: string;
  enabled: boolean;
  partial: boolean;
}) {
  return (
    <span
      title={`${label}: ${partial ? 'some' : enabled ? 'all' : 'off'}`}
      className={cn(
        'size-1.5 rounded-full',
        enabled ? (partial ? 'bg-muted-foreground/45' : 'bg-foreground') : 'bg-border',
      )}
    />
  );
}

function LoadingState() {
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="h-[104px] animate-pulse rounded-xl border bg-card" />
        ))}
      </div>
      <div className="h-[420px] animate-pulse rounded-xl border bg-card" />
    </div>
  );
}

function channelCount(
  events: NotificationPreferenceEvent[],
  draft: Draft,
  channel: NotificationChannelKey,
) {
  return events.filter((event) => (draft[event.eventType] ?? event.channels)[channel]).length;
}

function deriveCategoryRule(events: NotificationPreferenceEvent[], draft: Draft) {
  const followsMajority = (channel: NotificationChannelKey) => {
    const enabled = channelCount(events, draft, channel);
    if (enabled * 2 === events.length) return undefined;
    return enabled > events.length / 2;
  };
  return {
    inApp: followsMajority('inApp'),
    email: followsMajority('email'),
    sms: followsMajority('sms'),
    webPush: followsMajority('webPush'),
    mobilePush: followsMajority('mobilePush'),
  } satisfies Partial<NotificationPreferenceChannels>;
}

function getExceptions(
  events: NotificationPreferenceEvent[],
  draft: Draft,
  baseline: Partial<NotificationPreferenceChannels>,
) {
  return events.filter((event) => {
    const channels = draft[event.eventType] ?? event.channels;
    return CHANNELS.some(
      ({ key }) => baseline[key] !== undefined && channels[key] !== baseline[key],
    );
  });
}

function inferLegacyCategory(eventType: string): NotificationCategory {
  const prefix = eventType.split('.', 1)[0];
  if (prefix === 'lead') return 'leads';
  if (['appointment', 'visit', 'dispatch', 'route', 'booking'].includes(prefix)) {
    return 'scheduling';
  }
  if (['invoice', 'payment', 'billing', 'consent', 'qb_sync'].includes(prefix)) {
    return 'billing';
  }
  if (['estimate', 'proposal'].includes(prefix)) return 'sales';
  if (['contact', 'customer'].includes(prefix)) return 'contacts';
  if (['job', 'report', 'checklist', 'membership'].includes(prefix)) return 'jobs';
  if (['team', 'user', 'technician'].includes(prefix)) return 'team';
  return 'system';
}

function formatSavedAt(value: string) {
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));
}
